/**
 * Deterministic avatar renderer.
 *
 * Renders DiceBear v9 `avataaars` SVGs *locally* using `@dicebear/core` and
 * embeds them as data-URIs. This eliminates the dependency on the DiceBear
 * HTTP endpoint, which was being blocked/cached in some browsers and left the
 * preview blank. Every consumer (sidebar, office, people, home, DMs, profile
 * card) uses `renderUserAvatar(user)` — one shared resolver — so a saved
 * change is visible everywhere immediately.
 *
 * NOTE: The hair-style / accessory / clothing / eye / mouth value strings
 * below MUST match the actual enums exposed by `@dicebear/collection`
 * (`avataaars.schema.properties.<field>.items.enum`). We verified them with a
 * local Node script — options that do not appear there are silently dropped
 * by DiceBear, which is what caused the "controls do nothing" bug.
 */

import { createAvatar } from "@dicebear/core";
import { avataaars } from "@dicebear/collection";

const BACKEND =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) || "";

// ─── Option catalogs (human-readable labels → DiceBear values) ────────────────

export const SKIN_TONES = [
  { value: "FFDBB4", label: "Very light" },
  { value: "EDB98A", label: "Light" },
  { value: "FD9841", label: "Tan" },
  { value: "F8D25C", label: "Warm" },
  { value: "D08B5B", label: "Medium" },
  { value: "AE5D29", label: "Deep" },
  { value: "614335", label: "Dark" },
];

// Values MUST match avataaars.schema.properties.top.items.enum
export const HAIR_STYLES = [
  { value: "shortFlat", label: "Short flat" },
  { value: "shortWaved", label: "Short waved" },
  { value: "shortCurly", label: "Short curly" },
  { value: "shortRound", label: "Short round" },
  { value: "shaggy", label: "Shaggy" },
  { value: "sides", label: "Sides" },
  { value: "theCaesar", label: "Caesar" },
  { value: "straight01", label: "Long straight" },
  { value: "straight02", label: "Long straight 2" },
  { value: "curly", label: "Long curly" },
  { value: "bigHair", label: "Big hair" },
  { value: "bob", label: "Bob" },
  { value: "bun", label: "Bun" },
  { value: "dreads", label: "Dreads" },
  { value: "hat", label: "Hat" },
  { value: "hijab", label: "Hijab" },
  { value: "turban", label: "Turban" },
];

// Head coverings hide hair — hair color has no visible effect for these.
export const HAIR_HIDDEN_TOPS = new Set(["hat", "hijab", "turban", "winterHat1", "winterHat02", "winterHat03", "winterHat04"]);

export const HAIR_COLORS = [
  { value: "2c1b18", label: "Black" },
  { value: "4a312c", label: "Dark brown" },
  { value: "724133", label: "Brown" },
  { value: "b58143", label: "Light brown" },
  { value: "d6b370", label: "Honey" },
  { value: "e6cea8", label: "Blonde" },
  { value: "e8e1e1", label: "Platinum" },
  { value: "ac4b1f", label: "Auburn" },
  { value: "d3352e", label: "Red" },
  { value: "b1b5b6", label: "Silver" },
];

// Values MUST match avataaars.schema.properties.facialHair.items.enum
// "none" is a UI-only value that maps to facialHairProbability=0.
export const FACIAL_HAIRS = [
  { value: "none", label: "None" },
  { value: "beardLight", label: "Stubble" },
  { value: "beardMedium", label: "Short beard" },
  { value: "beardMajestic", label: "Full beard" },
  { value: "moustacheFancy", label: "Moustache (fancy)" },
  { value: "moustacheMagnum", label: "Moustache (magnum)" },
];

// facialHairColor is independent from hairColor per DiceBear v9 avataaars schema.
export const FACIAL_HAIR_COLORS = [
  { value: "2c1b18", label: "Black" },
  { value: "4a312c", label: "Dark brown" },
  { value: "724133", label: "Brown" },
  { value: "b58143", label: "Light brown" },
  { value: "d6b370", label: "Honey" },
  { value: "e8e1e1", label: "Grey" },
  { value: "ac4b1f", label: "Auburn" },
  { value: "c93305", label: "Red" },
];

// Values MUST match avataaars.schema.properties.eyebrows.items.enum
export const EYEBROWS = [
  { value: "default", label: "Neutral" },
  { value: "defaultNatural", label: "Natural" },
  { value: "flatNatural", label: "Flat" },
  { value: "raisedExcited", label: "Raised" },
  { value: "raisedExcitedNatural", label: "Raised (soft)" },
  { value: "angry", label: "Angry" },
  { value: "angryNatural", label: "Angry (soft)" },
  { value: "sadConcerned", label: "Sad" },
  { value: "sadConcernedNatural", label: "Sad (soft)" },
  { value: "upDown", label: "Up-down" },
  { value: "upDownNatural", label: "Up-down (soft)" },
  { value: "frownNatural", label: "Frown" },
  { value: "unibrowNatural", label: "Unibrow" },
];

// Values MUST match avataaars.schema.properties.eyes.items.enum
export const EYES = [
  { value: "default", label: "Neutral" },
  { value: "happy", label: "Happy" },
  { value: "squint", label: "Squint" },
  { value: "surprised", label: "Surprised" },
  { value: "wink", label: "Wink" },
  { value: "side", label: "Side" },
  { value: "hearts", label: "Hearts" },
  { value: "closed", label: "Closed" },
];

// Values MUST match avataaars.schema.properties.mouth.items.enum
export const MOUTHS = [
  { value: "default", label: "Neutral" },
  { value: "smile", label: "Smile" },
  { value: "twinkle", label: "Twinkle" },
  { value: "serious", label: "Serious" },
  { value: "concerned", label: "Concerned" },
  { value: "disbelief", label: "Disbelief" },
  { value: "sad", label: "Sad" },
  { value: "tongue", label: "Tongue" },
];

// Values MUST match avataaars.schema.properties.accessories.items.enum
// "none" is UI-only — it means "no accessory" (accessoriesProbability=0).
export const ACCESSORIES = [
  { value: "none", label: "None" },
  { value: "prescription01", label: "Glasses (round)" },
  { value: "prescription02", label: "Glasses (square)" },
  { value: "round", label: "Glasses (thin)" },
  { value: "sunglasses", label: "Sunglasses" },
  { value: "wayfarers", label: "Wayfarers" },
  { value: "eyepatch", label: "Eyepatch" },
  { value: "kurt", label: "Kurt frames" },
];

// Values MUST match avataaars.schema.properties.clothing.items.enum
export const CLOTHING = [
  { value: "shirtCrewNeck", label: "Crew neck" },
  { value: "shirtVNeck", label: "V-neck" },
  { value: "shirtScoopNeck", label: "Scoop neck" },
  { value: "graphicShirt", label: "Graphic tee" },
  { value: "hoodie", label: "Hoodie" },
  { value: "collarAndSweater", label: "Collar + sweater" },
  { value: "blazerAndShirt", label: "Blazer + shirt" },
  { value: "blazerAndSweater", label: "Blazer + sweater" },
  { value: "overall", label: "Overall" },
];

export const CLOTHING_COLORS = [
  { value: "0EA5E9", label: "Sky" },
  { value: "F97316", label: "Orange" },
  { value: "10B981", label: "Emerald" },
  { value: "8B5CF6", label: "Violet" },
  { value: "EF4444", label: "Red" },
  { value: "F59E0B", label: "Amber" },
  { value: "3B82F6", label: "Blue" },
  { value: "EC4899", label: "Pink" },
  { value: "14B8A6", label: "Teal" },
  { value: "111827", label: "Black" },
  { value: "6B7280", label: "Grey" },
  { value: "F3F4F6", label: "White" },
];

export const BACKGROUND_COLORS = [
  { value: "fef4e6", label: "Cream" },
  { value: "ffedd5", label: "Peach" },
  { value: "e0f2fe", label: "Sky" },
  { value: "fee2e2", label: "Rose" },
  { value: "dcfce7", label: "Mint" },
  { value: "f3e8ff", label: "Lilac" },
  { value: "fce7f3", label: "Pink" },
  { value: "f4f4f5", label: "Silver" },
  { value: "fef3c7", label: "Sunshine" },
  { value: "e5e7eb", label: "Fog" },
];

export const EXPRESSIONS = {
  neutral:  { eyes: "default",   mouth: "default" },
  happy:    { eyes: "happy",     mouth: "smile" },
  focused:  { eyes: "squint",    mouth: "serious" },
  busy:     { eyes: "side",      mouth: "concerned" },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function seedFromUser(u) {
  return u?.avatar_seed || u?.email || u?.id || u?.name || "u";
}

export function absolutePhotoUrl(url, version) {
  if (!url) return null;
  const v = version ? `?v=${encodeURIComponent(version)}` : "";
  if (/^https?:\/\//i.test(url)) return url + (url.includes("?") ? "" : v);
  if (url.startsWith("/")) return `${BACKEND}${url}${v}`;
  return url;
}

// Cache generated data-URIs so we don't re-render for identical inputs on every render.
const _svgCache = new Map();
const _CACHE_LIMIT = 400;

function cacheKey(cfg, seed) {
  return JSON.stringify([
    seed,
    cfg.skinColor, cfg.top, cfg.hairColor, cfg.eyebrows, cfg.eyes, cfg.mouth,
    cfg.accessories, cfg.clothing, cfg.clothesColor, cfg.backgroundColor,
    cfg.facialHair, cfg.facialHairColor,
  ]);
}

const VALID = {
  top: new Set(HAIR_STYLES.map((h) => h.value)),
  eyebrows: new Set(EYEBROWS.map((h) => h.value)),
  eyes: new Set(EYES.map((h) => h.value)),
  mouth: new Set(MOUTHS.map((h) => h.value)),
  accessories: new Set(ACCESSORIES.filter((a) => a.value !== "none").map((h) => h.value)),
  clothing: new Set(CLOTHING.map((h) => h.value)),
  facialHair: new Set(FACIAL_HAIRS.filter((f) => f.value !== "none").map((f) => f.value)),
};

/**
 * Build a data-URI SVG for the given user's illustrated avatar.
 * Fully deterministic and works offline (no HTTP request).
 *
 * IMPORTANT (release-candidate patch §5): facial hair defaults to NONE.
 * DiceBear v9 avataaars has `facialHairProbability: 10` by default, which is
 * why the previous release randomly grew beards on users who never chose one.
 * We always pass `facialHairProbability=0` unless the user explicitly picked a
 * value.
 */
export function illustratedUrl(user) {
  const cfg = user?.avatar_config || {};
  const seed = seedFromUser(user);
  const key = cacheKey(cfg, seed);
  const hit = _svgCache.get(key);
  if (hit) return hit;

  const opts = { seed, radius: 50 };
  opts.backgroundColor = [cfg.backgroundColor || "fef4e6"];
  if (cfg.skinColor) opts.skinColor = [cfg.skinColor];
  if (cfg.hairColor) opts.hairColor = [cfg.hairColor];
  if (cfg.clothesColor) opts.clothesColor = [cfg.clothesColor];
  if (cfg.top && VALID.top.has(cfg.top)) opts.top = [cfg.top];
  if (cfg.eyebrows && VALID.eyebrows.has(cfg.eyebrows)) opts.eyebrows = [cfg.eyebrows];
  if (cfg.eyes && VALID.eyes.has(cfg.eyes)) opts.eyes = [cfg.eyes];
  if (cfg.mouth && VALID.mouth.has(cfg.mouth)) opts.mouth = [cfg.mouth];
  if (cfg.clothing && VALID.clothing.has(cfg.clothing)) opts.clothing = [cfg.clothing];
  if (cfg.accessories && cfg.accessories !== "none" && VALID.accessories.has(cfg.accessories)) {
    opts.accessories = [cfg.accessories];
    opts.accessoriesProbability = 100;
  } else {
    opts.accessoriesProbability = 0;
  }
  // Facial hair — independent from hair style.
  if (cfg.facialHair && cfg.facialHair !== "none" && VALID.facialHair.has(cfg.facialHair)) {
    opts.facialHair = [cfg.facialHair];
    opts.facialHairProbability = 100;
    if (cfg.facialHairColor) opts.facialHairColor = [cfg.facialHairColor];
  } else {
    opts.facialHairProbability = 0;
  }

  const svg = createAvatar(avataaars, opts).toString();
  const dataUri = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

  if (_svgCache.size >= _CACHE_LIMIT) {
    // FIFO eviction — cheap: remove the first inserted key.
    const first = _svgCache.keys().next().value;
    if (first !== undefined) _svgCache.delete(first);
  }
  _svgCache.set(key, dataUri);
  return dataUri;
}

/**
 * Single shared resolver used by every avatar-rendering surface.
 * Priority: uploaded photo → illustrated (local SVG) → deterministic default.
 */
export function renderUserAvatar(user) {
  if (!user) return illustratedUrl(user);
  const v = user.updated_at || user.avatar_updated_at;
  if (user.avatar_type === "photo") {
    const photo = user.profile_photo_url || user.avatar_url;
    if (photo) return absolutePhotoUrl(photo, v);
  }
  if (user.avatar_type === "illustrated") return illustratedUrl(user);
  if (user.avatar_url && /^https?:\/\//i.test(user.avatar_url)) return user.avatar_url;
  if (user.avatar_url && user.avatar_url.startsWith("/api/")) return absolutePhotoUrl(user.avatar_url, v);
  return illustratedUrl(user);
}

// Legacy compat alias — same function as renderUserAvatar.
export const resolveAvatarUrl = renderUserAvatar;

// ─── Randomize / defaults ───────────────────────────────────────────────────

export function randomAvatarConfig() {
  const pick = (arr) => arr[Math.floor(Math.random() * arr.length)].value;
  return {
    skinColor: pick(SKIN_TONES),
    top: pick(HAIR_STYLES),
    hairColor: pick(HAIR_COLORS),
    eyebrows: pick(EYEBROWS),
    eyes: pick(EYES),
    mouth: pick(MOUTHS),
    accessories: pick(ACCESSORIES),
    clothing: pick(CLOTHING),
    clothesColor: pick(CLOTHING_COLORS),
    backgroundColor: pick(BACKGROUND_COLORS),
    // Facial hair defaults to None — never randomly assigned (spec §5A).
    facialHair: "none",
    facialHairColor: FACIAL_HAIR_COLORS[0].value,
  };
}

export const DEFAULT_AVATAR_CONFIG = {
  skinColor: SKIN_TONES[2].value,
  top: HAIR_STYLES[0].value,
  hairColor: HAIR_COLORS[1].value,
  eyebrows: EYEBROWS[0].value,
  eyes: EYES[0].value,
  mouth: MOUTHS[1].value,
  accessories: "none",
  clothing: CLOTHING[0].value,
  clothesColor: CLOTHING_COLORS[0].value,
  backgroundColor: BACKGROUND_COLORS[0].value,
  facialHair: "none",
  facialHairColor: FACIAL_HAIR_COLORS[0].value,
};
